import { useState } from 'react';

import { getInitials, MenuItem } from '@class-ui/react-components';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { Avatar, Box, ListItemText, Menu, styled, Typography } from '@mui/material';
import Divider, { dividerClasses } from '@mui/material/Divider';
import { listClasses } from '@mui/material/List';
import ListItemIcon, { listItemIconClasses } from '@mui/material/ListItemIcon';
import { paperClasses } from '@mui/material/Paper';
import { useTranslation } from 'react-i18next';

import { getUrls } from '../../../utils/url';

const StyledMenu = styled(Menu)`
    .${listClasses.root} {
        padding: '4px';
    }
    .${dividerClasses.root} {
        padding: 0;
    }
    .${paperClasses.root} {
        margin: '4px -4px';
    }
`;

const LogoutMenuItem = styled(MenuItem)`
    .${listItemIconClasses.root} {
        ml: 'auto',
        minWidth: 0
    }
`;

function stringAvatar(name: string, avatarUrl?: string) {
    if (avatarUrl) {
        return {
            sx: {
                'margin-left': '4px',
                'margin-top': '1px',
                width: '28px',
                height: '28px',
                cursor: 'pointer'
            },
            size: 'small',
            src: avatarUrl,
            alt: name
        };
    }
    return {
        sx: {
            'margin-left': '4px',
            bgcolor: 'rgb(71, 57, 231)',
            'border-width': 'thin',
            'border-color': 'white',
            'border-style': 'solid',
            color: 'white',
            cursor: 'pointer'
        },
        children: getInitials(name)
    };
}

const UserInfoBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
}));

const UserDetails = styled(Box)(() => ({
    overflow: 'hidden'
}));

const UserName = styled(Typography)(({ theme }) => ({
    fontWeight: 500,
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
}));

const UserEmail = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
}));

interface UserStateProps {
    name?: string;
    isGuest?: boolean;
    avatarUrl?: string;
    email?: string;
}

const AdminUserState = (userStateProps: UserStateProps) => {
    const { name, avatarUrl, email, isGuest } = userStateProps;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { t } = useTranslation('common');

    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleLogout = () => {
        console.log('Logging out');
        const { vanityUrl } = getUrls();
        localStorage.removeItem('user_uuid');
        window.location.href = vanityUrl;
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleClickSupport = () => {
        window.open(
            'https://class.com/support/class',
            `${t('class-support')}`,
            'title=Class support,width=1000,height=800,scrollbars=on'
        );
    };
    if (isGuest || !name) {
        return <></>;
    }

    return (
        <>
            <UserInfoBox>
                <Avatar alt={name} onClick={handleClick} {...stringAvatar(name, avatarUrl)} />
                <UserDetails>
                    <UserName>{name}</UserName>
                    <UserEmail>{email}</UserEmail>
                </UserDetails>
            </UserInfoBox>

            <StyledMenu
                anchorEl={anchorEl}
                id="menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={handleClickSupport} sx={{}}>
                    {t('class-support')}
                </MenuItem>
                <Divider />
                <LogoutMenuItem onClick={handleLogout}>
                    <ListItemText>{t('logout')}</ListItemText>
                    <ListItemIcon>
                        <LogoutRoundedIcon fontSize="small" />
                    </ListItemIcon>
                </LogoutMenuItem>
            </StyledMenu>
        </>
    );
};

export default AdminUserState;
// This code defines a UserState component that displays user information and provides a menu for logout and support.
